import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S14P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 14"
      />
      <DefaultSlide title="Agenda">
        <div style={{ fontSize: "0.75em" }}>
          <ul>
            <li>VueJS</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide title="VueJS" />
        <DefaultSlide title="¿Qué es?">
          <div style={{ fontSize: "0.75em" }}>
            <p>Vue.js es un framework progresivo de JavaScript para construir interfaces de usuario.</p>
            <p>Se puede usar para desarrollar aplicaciones de una sola página (SPA) y se integra fácilmente con otras bibliotecas o proyectos existentes.</p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Historia">
          <div>
            <img src="/images/S14/vuejscreatore.jpg" alt="VueJS Creator" style={{ width: "400px", height: "250px" }} />
          </div>
          <div style={{ fontSize: "0.75em" }}>
            <p>Creado por Evan You en 2014.</p>
            <p>Inspirado en AngularJS, pero con un enfoque más ligero y flexible.</p>
            <p>Ha crecido rápidamente en popularidad y cuenta con una gran comunidad y ecosistema de plugins.</p>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de una aplicación de VueJS (JS)"
          language="javascript">
          {`import { createApp, ref } from 'vue'

createApp({
  setup() {
    return {
      count: ref(0)
    }
  }
}).mount('#app')
          `}</CodeSlide>
        <CodeSlide
          title="Ejemplo de una aplicación de VueJS (HTML)"
          language="html">
          {`<div id="app">
  <button @click="count++">
    Count is: {{ count }}
  </button>
</div>`}</CodeSlide>
        <DefaultSlide title="Características Principales">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Reactividad: Actualiza automáticamente la interfaz de usuario cuando cambian los datos.</li>
              <li>Componentes: Permite crear componentes reutilizables y encapsulados.</li>
              <li>Directivas: Atributos especiales que proporcionan funcionalidades adicionales a los elementos del DOM.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ecosistema">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Vue Router: Manejo de rutas para aplicaciones de una sola página.</li>
              <li>Vuex: Manejo del estado global de la aplicación.</li>
              <li>Vue CLI: Herramienta para crear y gestionar proyectos Vue.js.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Ventajas">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Ligero y rápido</li>
              <li>Fácil de aprender y usar</li>
              <li>Gran comunidad y ecosistema</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Desventajas">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>No tan maduro como otros frameworks (ej. React, Angular)</li>
              <li>Menos recursos y documentación en comparación con otros frameworks</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Single File Components (SFC)">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              En Vue.js, los <strong>Single File Components</strong> (SFC) permiten definir la estructura, el estilo y la lógica de un componente en un solo archivo <code>.vue</code>.
            </p>
            <ul>
              <li>Separación clara de HTML, CSS y JavaScript en secciones <code>&lt;template&gt;</code>, <code>&lt;script&gt;</code> y <code>&lt;style&gt;</code>.</li>
              <li>Facilita la organización y reutilización de componentes.</li>
              <li>Soporta herramientas modernas como preprocessors y scoped styles.</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>
      <section>
        <TitleSlide title="Vue Cli" />
        <DefaultSlide title="¿Qué es Vue CLI?">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Vue CLI es una herramienta de línea de comandos para crear y gestionar proyectos Vue.js.
            </p>
            <ul>
              <li>Permite crear proyectos con una estructura predefinida.</li>
              <li>Incluye herramientas para desarrollo, pruebas y construcción.</li>
              <li>Soporta plugins y configuraciones personalizadas.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Instalación">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Para instalar Vue CLI, deben tener Node.js y npm instalados, luego ejecuta:
            </p>
            <pre><code className={'bash hljs'} data-trim data-line-numbers>{`npm install -g @vue/cli`}</code></pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creación de un Proyecto">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Para crear un nuevo proyecto Vue.js:
            </p>
            <pre><code className={'bash hljs'} data-trim data-line-numbers>{`vue create my-project`}</code></pre>
            <p>
              Sigan las instrucciones para seleccionar las configuraciones y plugins deseados.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Estructura del Proyecto">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La estructura del proyecto creado por Vue CLI incluye:
            </p>
            <ul>
              <li><code>src/</code>: Contiene los archivos fuente de la aplicación.</li>
              <li><code>public/</code>: Archivos estáticos que se sirven directamente.</li>
              <li><code>node_modules/</code>: Dependencias del proyecto.</li>
              <li><code>package.json</code>: Información del proyecto y sus dependencias.</li>
              <li><code>vue.config.js</code>: Configuración personalizada de Vue CLI.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Comandos Útiles">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li><code>npm run serve</code>: Inicia el servidor de desarrollo.</li>
              <li><code>npm run build</code>: Construye la aplicación para producción.</li>
              <li><code>npm run lint</code>: Ejecuta el linter para verificar el código.</li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <p>¡Vamos a crear una nueva aplicación Vue.js!</p>
          <p>Veamos la estructura y aprendamos sobre el proyecto.</p>
        </section>
      </section>

      <section>
        <TitleSlide title="Routing & Lazy Loading" />
        <DefaultSlide title="Vue Router">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              Vue Router es la biblioteca oficial de enrutamiento para Vue.js.
            </p>
            <ul>
              <li>Permite crear aplicaciones de una sola página (SPA) con múltiples vistas.</li>
              <li>Soporta navegación basada en rutas y parámetros dinámicos.</li>
              <li>Facilita la carga diferida (lazy loading) de componentes.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Lazy Loading">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              La carga diferida (lazy loading) permite cargar componentes solo cuando son necesarios, mejorando el rendimiento de la aplicación.
            </p>
            <ul>
              <li>Se puede implementar usando la función <code>import()</code> en las rutas del router.</li>
              <li>Reduce el tamaño inicial del paquete y acelera el tiempo de carga.</li>
              <li>Ideal para aplicaciones grandes con muchas vistas y componentes.</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>


      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de la clase" />
    </>
  );
}
